import { Controller, useForm } from "react-hook-form";
import "../account-manager.scss";
import { InputText } from "primereact/inputtext";
import { Tooltip } from "primereact/tooltip";
import { Dropdown } from "primereact/dropdown";
import { getData } from "country-list";
import ReactCountryFlag from "react-country-flag";
import { countryPhoneCodes } from "~/utils/countries";
import {
  AccountManagerFormHandle,
  AccountManagerFormInput,
  AccountManagerFormProps,
  CountryOption,
} from "~/Pages/Tenants/types/tenants";
import { forwardRef, useEffect, useImperativeHandle, useMemo } from "react";

const AccountManagerInfoForm = forwardRef<
  AccountManagerFormHandle,
  AccountManagerFormProps
>(
  (
    { onIsDirtyChange, accountManagerInfo, onSave, onErrorsChange, isEditing },
    ref,
  ) => {
    const {
      control,
      formState: { errors, isDirty, isValid },
      reset,
      handleSubmit,
    } = useForm<AccountManagerFormInput>({
      mode: "onChange",
      defaultValues: {
        given_name: "",
        other_name: "",
        surname: "",
        preferred_name: "",
        country: {},
        email: "",
        phone_code: "",
        phone_number: "",
      },
    });
    const countries = useMemo(() => getData(), []);
    const countryTemplate = (option: CountryOption) => {
      return (
        <div className="flex align-items-center">
          <ReactCountryFlag
            countryCode={option.code}
            svg
            className="flag-svg-icon"
          />
          <span>{option.name}</span>
        </div>
      );
    };
    const phoneCodeOptions = countryPhoneCodes.map((item) => ({
      label: `${item.dial_code} ${item.name}`,
      value: item.dial_code.replace("+", ""),
    }));
    const onSubmit = (data: AccountManagerFormInput) => {
      onSave(data);
    };

    useEffect(() => {
      if (accountManagerInfo) {
        const countrySelected = countries.find(
          (country: CountryOption) =>
            country.code === accountManagerInfo?.country,
        );
        reset({
          given_name: accountManagerInfo.given_name ?? "",
          other_name: accountManagerInfo?.other_name ?? "",
          surname: accountManagerInfo?.surname ?? "",
          preferred_name: accountManagerInfo?.preferred_name ?? "",
          country: countrySelected ?? "",
          email: accountManagerInfo?.email ?? "",
          phone_code: accountManagerInfo?.phone_code ?? "",
          phone_number: accountManagerInfo?.phone_number ?? "",
        });
      }
    }, [accountManagerInfo, countries, reset]);

    useImperativeHandle(ref, () => {
      return {
        submit: () => handleSubmit(onSubmit)(),
        reset: reset,
      };
    }, [handleSubmit, onSubmit]);

    useEffect(() => {
      onIsDirtyChange(isDirty);
    }, [isDirty, onIsDirtyChange]);

    useEffect(() => {
      onErrorsChange(isValid);
    }, [isValid, onErrorsChange]);

    return (
      <div className="account-manager-form flex flex-column gap-2">
        <p className="form-title m-0"> General Information </p>
        <form onSubmit={handleSubmit(onSubmit)}>
          <div className="row flex gap-3">
            <div className="flex flex-column col-6 pl-0 pr-0  gap-2">
              <label
                htmlFor="given_name"
                className="flex align-items-center gap-1"
              >
                Given name(s)*
                <span id="given-name-info" className="pi pi-info-circle"></span>
                <Tooltip
                  target="#given-name-info"
                  content="This is usually the person's first name or the name they usually go by."
                  position="right"
                  className="custom-tooltip tooltip-custom-format"
                />
              </label>
              <Controller
                name="given_name"
                control={control}
                rules={{
                  required: "Given Name is required",
                  pattern: {
                    value: /^[A-Za-z\s]+$/,
                    message: "Special characters are not allowed.",
                  },
                }}
                render={({ field }) => (
                  <InputText
                    {...field}
                    placeholder="Enter given name(s)"
                    type="text"
                    className={`input-field appearance-none outline-none w-full ${
                      errors.given_name ? "error" : ""
                    }`}
                    disabled={!isEditing}
                  />
                )}
              />
              {errors.given_name && (
                <small className="error-message">
                  {errors.given_name.message}
                </small>
              )}
            </div>
            <div className="flex flex-column col-6 pl-0 pr-0  gap-2">
              <label
                htmlFor="other_name"
                className="flex align-items-center gap-1"
              >
                Other name(s)
                <span id="other-name-info" className="pi pi-info-circle"></span>
                <Tooltip
                  target="#other-name-info"
                  content="Any middle or extra names that the employee has. You can leave this blank if it doesn’t apply."
                  position="right"
                  className="custom-tooltip tooltip-custom-format"
                />
              </label>
              <Controller
                name="other_name"
                control={control}
                rules={{
                  pattern: {
                    value: /^[A-Za-z\s]+$/,
                    message: "Special characters are not allowed.",
                  },
                }}
                render={({ field }) => (
                  <InputText
                    {...field}
                    placeholder="Enter any other names, such as middle names or clan names"
                    type="text"
                    className={`input-field appearance-none outline-none w-full ${
                      errors.other_name ? "error" : ""
                    }`}
                    disabled={!isEditing}
                  />
                )}
              />
              {errors.other_name && (
                <small className="error-message">
                  {errors.other_name.message}
                </small>
              )}
            </div>
          </div>

          <div className="row flex gap-3">
            <div className="flex flex-column col-6 pl-0 pr-0  gap-2">
              <label
                htmlFor="surname"
                className="flex align-items-center gap-1"
              >
                Surname(s)
                <span id="surname-info" className="pi pi-info-circle"></span>
                <Tooltip
                  target="#surname-info"
                  content="This is usually a family name or last name. If employee has more than one, include them here."
                  position="right"
                  className="custom-tooltip tooltip-custom-format"
                />
              </label>
              <Controller
                name="surname"
                control={control}
                rules={{
                  pattern: {
                    value: /^[A-Za-z\s]+$/,
                    message: "Special characters are not allowed.",
                  },
                }}
                render={({ field }) => (
                  <InputText
                    {...field}
                    placeholder="Enter surname(s)"
                    type="text"
                    className={`input-field appearance-none outline-none w-full ${
                      errors.surname ? "error" : ""
                    }`}
                    disabled={!isEditing}
                  />
                )}
              />
              {errors.surname && (
                <small className="error-message">
                  {errors.surname.message}
                </small>
              )}
            </div>
            <div className="flex flex-column col-6 pl-0 pr-0  gap-2">
              <label
                htmlFor="preferred_name"
                className="flex align-items-center gap-1"
              >
                Preferred name(s)
                <span
                  id="preferred-name-info"
                  className="pi pi-info-circle"
                ></span>
                <Tooltip
                  target="#preferred-name-info"
                  content="If the employee wants to be called by a different name than their given name, put that here."
                  position="right"
                  className="custom-tooltip tooltip-custom-format"
                />
              </label>
              <Controller
                name="preferred_name"
                control={control}
                rules={{
                  pattern: {
                    value: /^[A-Za-z\s]+$/,
                    message: "Special characters are not allowed.",
                  },
                }}
                render={({ field }) => (
                  <InputText
                    {...field}
                    placeholder="Preferred name or nickname"
                    type="text"
                    className={`input-field appearance-none outline-none w-full ${
                      errors.preferred_name ? "error" : ""
                    }`}
                    disabled={!isEditing}
                  />
                )}
              />
              {errors.preferred_name && (
                <small className="error-message">
                  {errors.preferred_name.message}
                </small>
              )}
            </div>
          </div>

          <div className="row flex gap-3">
            <div className="flex flex-column col-12 pl-0 pr-0 gap-2">
              <label
                htmlFor="country"
                className="flex align-items-center gap-1"
              >
                Country *
              </label>
              <Controller
                name="country"
                control={control}
                render={({ field }) => (
                  <Dropdown
                    {...field}
                    placeholder="Select country"
                    optionLabel="name"
                    options={countries}
                    itemTemplate={countryTemplate}
                    disabled={!isEditing}
                    className={`officer-dropdown-field w-full ${errors.country ? "error" : ""}`}
                  />
                )}
              />
            </div>
          </div>

          <div className="row flex gap-3">
            <div className="flex flex-column col-12 pl-0 pr-0  gap-2">
              <label htmlFor="email" className="flex align-items-center gap-1">
                Contact email *
              </label>
              <Controller
                name="email"
                control={control}
                rules={{
                  required: "Contact email is required",
                  pattern: {
                    value: /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+.[a-zA-Z]{2,}$/,
                    message: "Contact Email is invalid.",
                  },
                }}
                render={({ field }) => (
                  <InputText
                    {...field}
                    placeholder="Enter email"
                    className={`input-field appearance-none outline-none w-full ${
                      errors.email ? "error" : ""
                    }`}
                    disabled={!isEditing}
                  />
                )}
              />
              {errors.email && (
                <small className="error-message">{errors.email.message}</small>
              )}
            </div>
          </div>

          <div className="row flex gap-3">
            <div className="flex flex-column col-12 pl-0 pr-0 gap-2">
              <label
                htmlFor="phone_dode"
                className="flex align-items-center gap-1"
              >
                Phone number
              </label>
              <div className="flex">
                <div className="col-3 pl-0 pr-0">
                  <Controller
                    name="phone_code"
                    control={control}
                    render={({ field }) => (
                      <Dropdown
                        {...field}
                        placeholder="Country code"
                        optionLabel="label"
                        options={phoneCodeOptions}
                        disabled={!isEditing}
                        className={`officer-dropdown-field w-full ${errors.phone_code ? "error" : ""}`}
                      />
                    )}
                  />
                </div>
                <div className="col-9 pl-0 pr-0">
                  <Controller
                    name="phone_number"
                    control={control}
                    render={({ field }) => (
                      <InputText
                        {...field}
                        placeholder="Enter phone number"
                        className={`input-field appearance-none outline-none w-full ${
                          errors.phone_number ? "error" : ""
                        }`}
                        disabled={!isEditing}
                      />
                    )}
                  />
                </div>
              </div>
            </div>
          </div>
        </form>
      </div>
    );
  },
);

export default AccountManagerInfoForm;
